import { React, useRef, useState, useEffect } from 'react';
import { useAuth } from '../Components/AuthContext';
import { useNavigate } from 'react-router-dom';
import EditDeleteTable from '../Components/EditDeleteTable';
import AddEditHealthcareWorkerModal from '../Components/AddEditHealthcareWorkerModal';
import DeleteModal from '../Components/DeleteModal';
import styles from '../Styles/ViewOfUsersPages.module.css';

function ViewOfHCWs() {
    // State for storing healthcare workers
    const [rows, setRows] = useState([]);
    const [errorMessage, setErrorMessage] = useState(null);
    const url = process.env.REACT_APP_API_URL;

    const { token } = useAuth(); // Access token from AuthContext

    // Fetch healthcare workers data from API
    const fetchHealthcareWorkers = async () => {
        try {
            // Fetch both datasets
            const [hcwResponse, instResponse] = await Promise.all([
                fetch(`${url}/healthcare-worker/list?verified=true`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                fetch(`${url}/institution-account/list`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
            ]);

            const [hcwData, instData] = await Promise.all([
                hcwResponse.json(),
                instResponse.json(),
            ]);

            // Map institution ID to name for easy lookup
            const instMap = {};
            instData.forEach((inst) => {
                instMap[inst.id] = inst.institution_name;
            });

            // Inject institution_name into each healthcare worker
            const merged = hcwData.map((hcw) => ({
                ...hcw,
                institution_name:
                    instMap[hcw.institution_account_id] || 'Unknown',
            }));

            setRows(merged);
        } catch (error) {
            console.error(
                'Error fetching healthcare workers or institutions:',
                error
            );
            setErrorMessage(
                'Failed to fetch healthcare worker or institution data'
            );
        }
    };

    // Use effect to load data on component mount
    useEffect(() => {
        fetchHealthcareWorkers();
    }, []);

    // Define table columns with improved widths and cell renderers
    const columns = [
        { field: 'first_name', headerName: 'First Name', width: 100, flex: 1 },
        { field: 'last_name', headerName: 'Last Name', width: 100, flex: 1 },
        { field: 'email', headerName: 'Email', width: 300, flex: 1 },
        { field: 'profession', headerName: 'Profession', width: 150, flex: 1 },
        {
            field: 'clinic_name',
            headerName: 'Clinic Name',
            width: 150,
            flex: 1,
        },
        {
            field: 'institution_name',
            headerName: 'Institution Name',
            width: 200,
            flex: 1,
        },
        { field: 'department', headerName: 'Department', width: 150, flex: 1 },
        // commenting this out since we don't currently get the HCW location, would be hard to do in the app
        // {
        //     field: 'location',
        //     headerName: 'Initial Location',
        //     width: 180,
        //     renderCell: (params) => (
        //         <a
        //             href={`https://www.google.com/maps?q=${params.row.latitude_at_creation},${params.row.longitude_at_creation}`}
        //             className="linkToClick"
        //             target="_blank"
        //             rel="noopener noreferrer"
        //         >
        //             Location in Maps
        //         </a>
        //     ),
        // },
        { field: 'country_name', headerName: 'Country', width: 80 },
        {
            field: 'view_ai_conversations',
            headerName: 'View AI Conversations',
            width: 180,
            renderCell: (params) => (
                <a href={'/hcw-ai-chats'} className="linkToClick">
                    View AI Conversations
                </a>
            ),
            flex: 1,
        },
    ];

    // All users can edit or delete healthcare workers in this example
    const canEdit = () => true;
    const canDelete = () => true;

    // Handler to delete a healthcare worker
    const deleteHealthcareWorker = async (id) => {
        try {
            const response = await fetch(`${url}/healthcare-worker/${id}`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error('Failed to delete healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error deleting healthcare worker:', error);
        }
    };

    // Handler to add/edit a healthcare worker
    const addEditHealthcareWorker = async (data, id) => {
        if (id) {
            editHealthcareWorker(data, id);
        } else {
            addHealthcareWorker(data);
        }
    };

    const editHealthcareWorker = async (data, id) => {
        try {
            const response = await fetch(`${url}/healthcare-worker/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error('Failed to update healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error updating healthcare worker:', error);
        }
    };

    const addHealthcareWorker = async (data) => {
        try {
            // Set default latitude/longitude values if not provided
            data.latitude_at_creation = data.latitude_at_creation || 0.0;
            data.longitude_at_creation = data.longitude_at_creation || 0.0;
            const response = await fetch(`${url}/healthcare-worker`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                throw new Error('Failed to add healthcare worker');
            }
            fetchHealthcareWorkers();
        } catch (error) {
            console.error('Error adding healthcare worker:', error);
        }
    };

    // Reference for the Add/Edit modal trigger
    const openAddHealthcareWorkerModal = useRef(null);

    return (
        <div className={styles.viewOfUsersContainer}>
            <h2>Healthcare Workers</h2>

            {errorMessage && <div className="alert error">{errorMessage}</div>}

            {/* no adding a HCW on the website because you need to set values
            for security questions and answers in account sign-up */}
            {/* <button
                className={styles.addButton}
                onClick={() => {
                    if (openAddHealthcareWorkerModal.current) {
                        openAddHealthcareWorkerModal.current();
                    }
                }}
            >
                Add Healthcare Worker
            </button> */}
            <div
                className={styles.tableWrapper}
                styles={{ marginLeft: '20px', marginRight: '20px' }}
            >
                <EditDeleteTable
                    rows={rows}
                    columns={columns}
                    editModal={AddEditHealthcareWorkerModal}
                    deleteModal={DeleteModal}
                    showEditButton={canEdit()}
                    showDeleteButton={canDelete()}
                    customConfirmMessage={(row) => {
                        // Construct a custom confirmation message dynamically based on the available data in the row.
                        // Each part of the message (name, profession, clinic, country) is only included if the corresponding value exists.
                        // If a value is missing, that part is omitted entirely to keep the message clean.

                        const name =
                            row?.first_name && row?.last_name
                                ? `${row.first_name} ${row.last_name}`
                                : ''; // Full name
                        const profession = row?.profession
                            ? `(${row.profession})`
                            : ''; // Profession in parentheses
                        const clinic = row?.clinic_name
                            ? `from ${row.clinic_name}`
                            : ''; // Clinic name prefixed with "from"
                        const country = row?.country_name
                            ? `in ${row.country_name}`
                            : ''; // Country name prefixed with "in"

                        // Combine all parts into a single string, separated by spaces.
                        // Empty or undefined parts are filtered out using .filter(Boolean).
                        return [name, profession, clinic, country]
                            .filter(Boolean)
                            .join(' ');
                    }}
                    onAdd={(handler) => {
                        openAddHealthcareWorkerModal.current = handler;
                    }}
                    onEdit={addEditHealthcareWorker}
                    onConfirmDelete={deleteHealthcareWorker}
                />
            </div>
        </div>
    );
}

export default ViewOfHCWs;
